import React, { useState } from 'react';
import { 
  Card, 
  Form, 
  Input, 
  Switch, 
  Button, 
  Space, 
  Divider, 
  Select, 
  InputNumber,
  Upload,
  message,
  Tabs,
  Alert,
  Typography,
  Row,
  Col,
} from 'antd';
import { 
  SaveOutlined, 
  UploadOutlined, 
  DownloadOutlined,
  ReloadOutlined,
  SettingOutlined,
  UserOutlined,
  SecurityScanOutlined,
  CloudUploadOutlined,
} from '@ant-design/icons';

const { Title, Text } = Typography;
const { Option } = Select;
const { TabPane } = Tabs;

interface UserSettings {
  name: string;
  email: string;
  company: string;
  role: string;
  language: string;
  theme: 'light' | 'dark';
  notifications: {
    email: boolean;
    push: boolean;
    dppCreated: boolean;
    validationComplete: boolean;
    scanCompleted: boolean;
  };
}

interface SystemSettings {
  apiEndpoint: string;
  apiKey: string;
  blockchainEnabled: boolean;
  llmEnabled: boolean;
  autoValidation: boolean;
  qrCodeSize: number;
  exportFormat: 'json' | 'xml' | 'pdf';
  retentionDays: number;
}

const Settings: React.FC = () => {
  const [activeTab, setActiveTab] = useState('user');
  const [loading, setLoading] = useState(false);
  const [userSettings, setUserSettings] = useState<UserSettings>({
    name: 'Max Mustermann',
    email: 'max.mustermann@company.com',
    company: 'Automotive Parts GmbH',
    role: 'DPP Manager',
    language: 'de',
    theme: 'light',
    notifications: {
      email: true,
      push: true,
      dppCreated: true,
      validationComplete: true,
      scanCompleted: false,
    },
  });

  const [systemSettings, setSystemSettings] = useState<SystemSettings>({
    apiEndpoint: 'https://api.dpp.company.com/v1',
    apiKey: 'sk-1234567890abcdef',
    blockchainEnabled: true,
    llmEnabled: true,
    autoValidation: true,
    qrCodeSize: 256,
    exportFormat: 'pdf',
    retentionDays: 365,
  });

  const handleUserSettingsSave = async () => {
    setLoading(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      message.success('Benutzereinstellungen gespeichert');
    } catch (error) {
      message.error('Fehler beim Speichern der Benutzereinstellungen');
    } finally {
      setLoading(false);
    }
  };

  const handleSystemSettingsSave = async () => {
    setLoading(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      message.success('Systemeinstellungen gespeichert');
    } catch (error) {
      message.error('Fehler beim Speichern der Systemeinstellungen');
    } finally {
      setLoading(false);
    }
  };

  const handleExportSettings = () => {
    const settings = {
      user: userSettings,
      system: systemSettings,
    };
    const blob = new Blob([JSON.stringify(settings, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'dpp-settings.json';
    a.click();
    URL.revokeObjectURL(url);
    message.success('Einstellungen exportiert');
  };

  const handleImportSettings = (file: any) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const settings = JSON.parse(e.target?.result as string);
        if (settings.user) setUserSettings(settings.user);
        if (settings.system) setSystemSettings(settings.system);
        message.success('Einstellungen importiert');
      } catch (error) {
        message.error('Fehler beim Importieren der Einstellungen');
      }
    };
    reader.readAsText(file);
    return false; // Prevent upload
  };

  const handleResetSettings = () => {
    setUserSettings({
      name: 'Max Mustermann',
      email: 'max.mustermann@company.com',
      company: 'Automotive Parts GmbH',
      role: 'DPP Manager',
      language: 'de',
      theme: 'light',
      notifications: {
        email: true,
        push: true,
        dppCreated: true,
        validationComplete: true,
        scanCompleted: false,
      },
    });
    message.info('Einstellungen zurückgesetzt');
  };

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto' }}>
      <Title level={2}>Einstellungen</Title>
      
      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane tab={<span><UserOutlined />Benutzer</span>} key="user">
          <Card>
            <Form layout="vertical" initialValues={userSettings}>
              <Title level={4}>Persönliche Informationen</Title>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item label="Name" name="name">
                    <Input 
                      value={userSettings.name}
                      onChange={(e) => setUserSettings(prev => ({ ...prev, name: e.target.value }))}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="E-Mail" name="email">
                    <Input 
                      type="email"
                      value={userSettings.email}
                      onChange={(e) => setUserSettings(prev => ({ ...prev, email: e.target.value }))}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item label="Unternehmen" name="company">
                    <Input 
                      value={userSettings.company}
                      onChange={(e) => setUserSettings(prev => ({ ...prev, company: e.target.value }))}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Rolle" name="role">
                    <Select 
                      value={userSettings.role}
                      onChange={(value) => setUserSettings(prev => ({ ...prev, role: value }))}
                    >
                      <Option value="DPP Manager">DPP Manager</Option>
                      <Option value="Quality Engineer">Quality Engineer</Option>
                      <Option value="Supply Chain Manager">Supply Chain Manager</Option>
                      <Option value="Administrator">Administrator</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Divider />

              <Title level={4}>Anzeigeeinstellungen</Title>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item label="Sprache" name="language">
                    <Select 
                      value={userSettings.language}
                      onChange={(value) => setUserSettings(prev => ({ ...prev, language: value }))}
                    >
                      <Option value="de">Deutsch</Option>
                      <Option value="en">English</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Theme" name="theme">
                    <Select 
                      value={userSettings.theme}
                      onChange={(value) => setUserSettings(prev => ({ ...prev, theme: value as 'light' | 'dark' }))}
                    >
                      <Option value="light">Hell</Option>
                      <Option value="dark">Dunkel</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Divider />

              <Title level={4}>Benachrichtigungen</Title>
              <Row gutter={16}>
                <Col span={12}>
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Form.Item label="E-Mail-Benachrichtigungen">
                      <Switch 
                        checked={userSettings.notifications.email}
                        onChange={(checked) => setUserSettings(prev => ({
                          ...prev,
                          notifications: { ...prev.notifications, email: checked }
                        }))}
                      />
                    </Form.Item>
                    <Form.Item label="Push-Benachrichtigungen">
                      <Switch 
                        checked={userSettings.notifications.push}
                        onChange={(checked) => setUserSettings(prev => ({
                          ...prev,
                          notifications: { ...prev.notifications, push: checked }
                        }))}
                      />
                    </Form.Item>
                  </Space>
                </Col>
                <Col span={12}>
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Form.Item label="DPP erstellt">
                      <Switch 
                        checked={userSettings.notifications.dppCreated}
                        onChange={(checked) => setUserSettings(prev => ({
                          ...prev,
                          notifications: { ...prev.notifications, dppCreated: checked }
                        }))}
                      />
                    </Form.Item>
                    <Form.Item label="Validierung abgeschlossen">
                      <Switch 
                        checked={userSettings.notifications.validationComplete}
                        onChange={(checked) => setUserSettings(prev => ({
                          ...prev,
                          notifications: { ...prev.notifications, validationComplete: checked }
                        }))}
                      />
                    </Form.Item>
                    <Form.Item label="Scan abgeschlossen">
                      <Switch 
                        checked={userSettings.notifications.scanCompleted}
                        onChange={(checked) => setUserSettings(prev => ({
                          ...prev,
                          notifications: { ...prev.notifications, scanCompleted: checked }
                        }))}
                      />
                    </Form.Item>
                  </Space>
                </Col>
              </Row>

              <Divider />

              <Space>
                <Button 
                  type="primary" 
                  icon={<SaveOutlined />}
                  onClick={handleUserSettingsSave}
                  loading={loading}
                >
                  Speichern
                </Button>
                <Button 
                  icon={<ReloadOutlined />}
                  onClick={handleResetSettings}
                >
                  Zurücksetzen
                </Button>
              </Space>
            </Form>
          </Card>
        </TabPane>

        <TabPane tab={<span><SettingOutlined />System</span>} key="system">
          <Card>
            <Alert
              message="Systemeinstellungen"
              description="Änderungen an den Systemeinstellungen erfordern möglicherweise einen Neustart der Anwendung."
              type="info"
              showIcon
              style={{ marginBottom: 24 }}
            />

            <Form layout="vertical" initialValues={systemSettings}>
              <Title level={4}>API-Konfiguration</Title>
              <Row gutter={16}>
                <Col span={16}>
                  <Form.Item label="API Endpunkt" name="apiEndpoint">
                    <Input 
                      value={systemSettings.apiEndpoint}
                      onChange={(e) => setSystemSettings(prev => ({ ...prev, apiEndpoint: e.target.value }))}
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label="API Schlüssel" name="apiKey">
                    <Input.Password 
                      value={systemSettings.apiKey}
                      onChange={(e) => setSystemSettings(prev => ({ ...prev, apiKey: e.target.value }))}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Divider />

              <Title level={4}>Funktionen</Title>
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item label="Blockchain aktivieren">
                    <Switch 
                      checked={systemSettings.blockchainEnabled}
                      onChange={(checked) => setSystemSettings(prev => ({ ...prev, blockchainEnabled: checked }))}
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label="LLM-Integration aktivieren">
                    <Switch 
                      checked={systemSettings.llmEnabled}
                      onChange={(checked) => setSystemSettings(prev => ({ ...prev, llmEnabled: checked }))}
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label="Auto-Validierung">
                    <Switch 
                      checked={systemSettings.autoValidation}
                      onChange={(checked) => setSystemSettings(prev => ({ ...prev, autoValidation: checked }))}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Divider />

              <Title level={4}>Export-Einstellungen</Title>
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item label="QR-Code Größe" name="qrCodeSize">
                    <InputNumber 
                      min={128}
                      max={512}
                      step={32}
                      value={systemSettings.qrCodeSize}
                      onChange={(value) => setSystemSettings(prev => ({ ...prev, qrCodeSize: value || 256 }))}
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label="Standard-Exportformat" name="exportFormat">
                    <Select 
                      value={systemSettings.exportFormat}
                      onChange={(value) => setSystemSettings(prev => ({ ...prev, exportFormat: value as 'json' | 'xml' | 'pdf' }))}
                    >
                      <Option value="json">JSON</Option>
                      <Option value="xml">XML</Option>
                      <Option value="pdf">PDF</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label="Datenaufbewahrung (Tage)" name="retentionDays">
                    <InputNumber 
                      min={30}
                      max={3650}
                      value={systemSettings.retentionDays}
                      onChange={(value) => setSystemSettings(prev => ({ ...prev, retentionDays: value || 365 }))}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Divider />

              <Space>
                <Button 
                  type="primary" 
                  icon={<SaveOutlined />}
                  onClick={handleSystemSettingsSave}
                  loading={loading}
                >
                  Speichern
                </Button>
                <Button 
                  icon={<ReloadOutlined />}
                  onClick={() => window.location.reload()}
                >
                  Anwendung neu starten
                </Button>
              </Space>
            </Form>
          </Card>
        </TabPane>

        <TabPane tab={<span><SecurityScanOutlined />Sicherheit</span>} key="security">
          <Card>
            <Title level={4}>Sicherheitseinstellungen</Title>
            <Alert
              message="Hinweis"
              description="Ändern Sie diese Einstellungen nur, wenn Sie mit den Auswirkungen vertraut sind."
              type="warning"
              showIcon
              style={{ marginBottom: 24 }}
            />

            <Space direction="vertical" style={{ width: '100%' }}>
              <Form.Item label="Zwei-Faktor-Authentifizierung">
                <Switch defaultChecked />
              </Form.Item>
              <Form.Item label="Session-Timeout (Minuten)">
                <InputNumber min={5} max={480} defaultValue={30} />
              </Form.Item>
              <Form.Item label="IP-Whitelist aktivieren">
                <Switch />
              </Form.Item>
              <Form.Item label="Audit-Logging aktivieren">
                <Switch defaultChecked />
              </Form.Item>
            </Space>

            <Divider />

            <Title level={4}>Daten-Backup</Title>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Form.Item label="Automatisches Backup">
                <Switch defaultChecked />
              </Form.Item>
              <Form.Item label="Backup-Frequenz">
                <Select defaultValue="daily">
                  <Option value="hourly">Stündlich</Option>
                  <Option value="daily">Täglich</Option>
                  <Option value="weekly">Wöchentlich</Option>
                  <Option value="monthly">Monatlich</Option>
                </Select>
              </Form.Item>
              <Form.Item label="Backup-Aufbewahrung (Tage)">
                <InputNumber min={7} max={365} defaultValue={30} />
              </Form.Item>
            </Space>

            <Divider />

            <Space>
              <Button type="primary" icon={<SaveOutlined />}>
                Speichern
              </Button>
              <Button icon={<CloudUploadOutlined />}>
                Jetzt Backup erstellen
              </Button>
            </Space>
          </Card>
        </TabPane>

        <TabPane tab={<span><CloudUploadOutlined />Import/Export</span>} key="import-export">
          <Card>
            <Title level={4}>Einstellungen importieren/exportieren</Title>
            
            <Row gutter={16}>
              <Col span={12}>
                <Card size="small" title="Exportieren">
                  <Text>Speichern Sie alle aktuellen Einstellungen in einer JSON-Datei.</Text>
                  <div style={{ marginTop: 16 }}>
                    <Button 
                      type="primary" 
                      icon={<DownloadOutlined />}
                      onClick={handleExportSettings}
                    >
                      Einstellungen exportieren
                    </Button>
                  </div>
                </Card>
              </Col>
              <Col span={12}>
                <Card size="small" title="Importieren">
                  <Text>Laden Sie Einstellungen aus einer JSON-Datei.</Text>
                  <div style={{ marginTop: 16 }}>
                    <Upload
                      accept=".json"
                      beforeUpload={handleImportSettings}
                      showUploadList={false}
                    >
                      <Button icon={<UploadOutlined />}>
                        Einstellungen importieren
                      </Button>
                    </Upload>
                  </div>
                </Card>
              </Col>
            </Row>

            <Divider />

            <Title level={4}>Daten-Import</Title>
            <Alert
              message="Daten-Import"
              description="Importieren Sie DPP-Daten aus verschiedenen Formaten (CSV, JSON, XML)."
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />

            <Space>
              <Upload>
                <Button icon={<UploadOutlined />}>
                  CSV-Datei importieren
                </Button>
              </Upload>
              <Upload>
                <Button icon={<UploadOutlined />}>
                  JSON-Datei importieren
                </Button>
              </Upload>
              <Upload>
                <Button icon={<UploadOutlined />}>
                  XML-Datei importieren
                </Button>
              </Upload>
            </Space>
          </Card>
        </TabPane>
      </Tabs>
    </div>
  );
};

export default Settings;
